import type { RunRecord } from '@orgsim/shared';
import { create } from 'zustand';
import { ApiError } from '../api/client';
import { type RunSummary, putRun } from '../api/runs';
import { useSession } from '../api/session';

// 'forbidden': the server holds this run under another account and refuses the update.
export type SyncState = 'synced' | 'pending' | 'failed' | 'unauthenticated' | 'forbidden';

const SYNC_INTERVAL_MS = 2000;

function stateFor(error: unknown): SyncState {
  if (error instanceof ApiError && error.status === 401) return 'unauthenticated';
  if (error instanceof ApiError && error.status === 403) return 'forbidden';
  return 'failed';
}

// IndexedDB stays the leading copy of a run; the server gets the full record at most every
// SYNC_INTERVAL_MS, and right away once the run is finished. A failed send is not retried on
// its own: the next event sends the (then newer) full record again.
// Only the focused run (the one on screen) reports its state. A run left behind with an
// unsent record still gets that record sent, ahead of anything else.
export function createSyncer(
  send: (record: RunRecord) => Promise<{ updatedAt: number }>,
  report: (state: SyncState) => void,
  clock = {
    now: () => Date.now(),
    setTimeout: (fn: () => void, ms: number): unknown => setTimeout(fn, ms),
    clearTimeout: (handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  },
) {
  let focused: string | null = null;
  let latest: RunRecord | null = null; // newest unsent record of the focused run
  const leftBehind = new Map<string, RunRecord>(); // newest unsent record per other run
  let lastSentAt = Number.NEGATIVE_INFINITY;
  let timer: unknown = null;
  // One request at a time, so an older record can never overtake a newer one on the wire.
  let inFlight = false;

  const clearTimer = () => {
    if (timer !== null) clock.clearTimeout(timer);
    timer = null;
  };

  const transmit = async (record: RunRecord) => {
    inFlight = true;
    try {
      await send(record);
      if (record.id === focused) report(latest ? 'pending' : 'synced');
    } catch (e) {
      if (record.id === focused) report(stateFor(e));
    } finally {
      inFlight = false;
      schedule();
    }
  };

  const sendLatest = () => {
    clearTimer();
    if (inFlight || !latest) return;
    const record = latest;
    latest = null;
    lastSentAt = clock.now();
    void transmit(record);
  };

  const schedule = () => {
    if (inFlight) return;
    const [behind] = leftBehind.values();
    if (behind) {
      leftBehind.delete(behind.id);
      void transmit(behind);
      return;
    }
    if (!latest) return;
    if (latest.finishedAt) return sendLatest();
    if (timer !== null) return;
    const wait = lastSentAt + SYNC_INTERVAL_MS - clock.now();
    if (wait <= 0) sendLatest();
    else timer = clock.setTimeout(sendLatest, wait);
  };

  const focus = (runId: string) => {
    if (runId === focused) return;
    if (latest) leftBehind.set(latest.id, latest);
    // A record queued for this run is superseded by the full record that follows.
    leftBehind.delete(runId);
    latest = null;
    clearTimer();
    lastSentAt = Number.NEGATIVE_INFINITY;
    focused = runId;
    schedule();
  };

  return {
    focus,
    sync(record: RunRecord) {
      focus(record.id);
      latest = record;
      report('pending');
      schedule();
    },
  };
}

export const useSyncStatus = create<{ state: SyncState }>(() => ({ state: 'synced' }));

// A 401 also ends the session (see request() in api/client.ts). The run screen stays usable
// (see sessionGate.ts); leaving it leads to the login.
const syncer = createSyncer(putRun, (state) => useSyncStatus.setState({ state }));

// A run whose server state is unknown (the run list could not be loaded) is never sent:
// the local copy might be older than the server's and would overwrite it.
let heldRunId: string | null = null;

// Called when a run is opened: its status starts clean instead of showing the previous run's.
export function focusSync(runId: string): void {
  heldRunId = null;
  syncer.focus(runId);
  useSyncStatus.setState({ state: 'synced' });
}

export function holdSync(runId: string): void {
  heldRunId = runId;
  useSyncStatus.setState({ state: 'failed' });
}

export function syncRun(record: RunRecord): void {
  // Without a session there is nobody to send to: an offline import runs on IndexedDB alone,
  // and trying the server on every event would only produce failing requests.
  const session = useSession.getState().state;
  if (session !== 'ready' || record.id === heldRunId) {
    useSyncStatus.setState({ state: session === 'anonymous' ? 'unauthenticated' : 'failed' });
    return;
  }
  syncer.sync(record);
}

// A run on the start screen: the full local record, or the server's summary of it.
export type ListedRun = RunRecord | RunSummary;

export const eventCountOf = (run: ListedRun) =>
  'events' in run ? run.events.length : run.eventCount;

// Same id on both sides: the copy with more events wins; on a tie the finished one, else the
// server's. Newest first.
export function mergeRuns(local: RunRecord[], remote: RunSummary[]): ListedRun[] {
  const remoteById = new Map(remote.map((r) => [r.id, r]));
  const byId = new Map<string, ListedRun>(remoteById);
  for (const l of local) {
    const r = remoteById.get(l.id);
    const tie = r && l.events.length === r.eventCount;
    if (!r || l.events.length > r.eventCount || (tie && l.finishedAt && !r.finishedAt)) {
      byId.set(l.id, l);
    }
  }
  return [...byId.values()].sort((a, b) => b.startedAt - a.startedAt);
}

// The record to open: a local copy that won the merge as it is, else the full run from the
// server, since the run list only carries summaries.
export const loadListedRun = (
  run: ListedRun,
  fetchRun: (id: string) => Promise<RunRecord>,
): Promise<RunRecord> => ('events' in run ? Promise.resolve(run) : fetchRun(run.id));

// Finished runs whose local copy won the merge, so their last upload never reached the server.
// Only runs the server already holds for this user are sent: a run only this browser knows may
// belong to someone else who used the same machine.
export function finishedUploads(
  local: RunRecord[],
  remote: RunSummary[],
  userId: string,
): RunRecord[] {
  const ownerOf = new Map(remote.map((r) => [r.id, r.userId]));
  return mergeRuns(local, remote).filter(
    (r): r is RunRecord => 'events' in r && Boolean(r.finishedAt) && ownerOf.get(r.id) === userId,
  );
}
